"use client";

import { useState, useRef, useEffect, useLayoutEffect } from "react";
import Link from "next/link";
import { IoMenuOutline } from "react-icons/io5";
import { CategoryPanel } from "../../panel/CategoryPanel";
import { CategoryDetailCard } from "../../panel/CategoryDetailCard";
import { CategoryPanelMobile } from "../../panel/CategoryPanelMobile";
import { findCategoryPath } from "../../../lib/resolve-category-path";
import { useViewportMaxHeight } from "../../../hooks/useViewportMaxHeight";
import { ScrollableContainer } from "@/shared/components/ScrollableContainer";
import { Icon } from "@/shared/components/Icon";
import { useEffectOnce } from "@/shared/hooks/useEffectOnce";
import { useIsMobile } from "@/shared/hooks/useIsMobile";
import { useTheme } from "@/theme";
import type { CategoryNavItem, CategoryNavVariantProps } from "../../../types/category-nav";
import { cn } from "@dionatanbig2be/big2be-ui/utils";

/** Mesmo corte do CategoryPanelClassic: abaixo disso não há hover e a aba só navega. */
const TAB_DROPDOWN_MIN_WIDTH = 801;
/** Tempo para o mouse sair da aba e chegar no dropdown sem ele fechar no caminho. */
const TAB_DROPDOWN_CLOSE_DELAY_MS = 150;

interface HoveredTab {
    category: CategoryNavItem;
    /** Borda esquerda da aba, relativa ao container da barra. */
    left: number;
}

/**
 * CategoryNav — variant-default (porte fiel do category-navigation deprecado):
 * barra full-width `bg-secondary`, primeiro item "DEPARTAMENTOS" que abre o
 * CategoryPanel, demais itens links diretos em uppercase.
 *
 * O scroll horizontal (gestos wheel/touch, setas auto-ocultáveis) é delegado ao
 * `ScrollableContainer` (`navVariant="edge-fade"`): as setas ganham um gradiente
 * que desvanece a partir da cor do fundo, lida da CSS var `--nav-fade-color`
 * (aqui apontada para `--color-secondary`, sem hardcode).
 *
 * Com `highlightItems` (destaques curados do portal), as abas exibem esses
 * itens no lugar das categorias raiz — com fundo mais claro e ícone; o painel
 * "Departamentos" continua com a árvore completa.
 *
 * No desktop, passar o mouse numa aba com subcategorias abre, embaixo dela, o
 * mesmo card do "Departamentos" (CategoryDetailCard) filtrado àquela categoria.
 * O clique na aba continua navegando. O dropdown é desenhado fora do
 * ScrollableContainer: ele rola na horizontal e recortaria o que passa da borda.
 * No celular não há hover: o toque numa aba com subcategorias abre o painel de
 * departamentos (CategoryPanelMobile) já nela, e o "Ver todas" dele navega.
 */
export function CategoryNavDefault({
    categories,
    highlightItems,
    layout = "classico",
    departmentsLabel = "Departamentos",
}: CategoryNavVariantProps) {
    const [open, setOpen] = useState(false);
    const [hoveredTab, setHoveredTab] = useState<HoveredTab | null>(null);
    const [dropdownLeft, setDropdownLeft] = useState(0);
    /** Pilha de ids com que o painel do celular abre (null = fechado). */
    const [mobilePanelPath, setMobilePanelPath] = useState<string[] | null>(null);
    const navRef = useRef<HTMLElement>(null);
    const barRef = useRef<HTMLDivElement>(null);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const closeTimer = useRef<ReturnType<typeof setTimeout>>(undefined);
    const isMobile = useIsMobile(TAB_DROPDOWN_MIN_WIDTH);
    // Mesmo teto do mega-menu: o dropdown nasce no header sticky e não pode
    // passar da borda inferior da tela (a lista de subcategorias rola por dentro).
    const dropdownMaxHeight = useViewportMaxHeight(dropdownRef, { enabled: hoveredTab !== null });
    const [headerWithExtraFooter, setHeaderWithExtraFooter] = useState(false);
    // Onde o botão de categorias aparece — mesma configuração que o Header lê
    // (highlights.categoriesTrigger). Sem valor salvo, mantém o comportamento
    // atual (botão aqui, na barra).
    const highlights = useTheme()?.structure?.highlights;
    const categoriesTrigger = highlights?.categoriesTrigger ?? "navbar";
    const showOwnButton = categoriesTrigger !== "search" && categoriesTrigger !== "hidden";
    const departmentsIcon = highlights?.departmentsIcon;

    // Lê marcações de outro componente (header) no DOM — só existem após a
    // montagem, por isso a checagem depende de um efeito, não de props.
    useEffectOnce(() => {
        const headerVariant3FooterRounded = document.querySelector(
            'header[data-variant="variant-3"][data-rounded-footer="true"]'
        );
        setHeaderWithExtraFooter(headerVariant3FooterRounded !== null);
    });

    // Fecha o painel ao clicar fora ou apertar Escape.
    useEffect(() => {
        if (!open) return;
        const onClickOutside = (e: MouseEvent) => {
            if (navRef.current && !navRef.current.contains(e.target as Node)) setOpen(false);
        };
        const onKey = (e: KeyboardEvent) => {
            if (e.key === "Escape") setOpen(false);
        };
        document.addEventListener("mousedown", onClickOutside);
        document.addEventListener("keydown", onKey);
        return () => {
            document.removeEventListener("mousedown", onClickOutside);
            document.removeEventListener("keydown", onKey);
        };
    }, [open]);

    useEffect(() => () => clearTimeout(closeTimer.current), []);

    // Aba perto da borda direita: o dropdown encosta na borda em vez de sair da barra.
    useLayoutEffect(() => {
        if (!hoveredTab || !barRef.current || !dropdownRef.current) return;
        const maxLeft = barRef.current.clientWidth - dropdownRef.current.offsetWidth;
        setDropdownLeft(Math.max(0, Math.min(hoveredTab.left, maxLeft)));
    }, [hoveredTab]);

    function openTabDropdown(category: CategoryNavItem, tab: HTMLElement) {
        clearTimeout(closeTimer.current);
        // Com o "Departamentos" aberto, os dois painéis disputariam o mesmo espaço.
        if (isMobile || open || !category.children?.length || !barRef.current) {
            setHoveredTab(null);
            return;
        }
        const barLeft = barRef.current.getBoundingClientRect().left;
        setHoveredTab({ category, left: tab.getBoundingClientRect().left - barLeft });
    }

    function handleTabClick(event: React.MouseEvent, category: CategoryNavItem) {
        setHoveredTab(null);
        if (!isMobile || !category.children?.length) return;

        // Fora da árvore carregada não há como abrir o painel nela: segue o link.
        const path = findCategoryPath(categories, category.id);
        if (path.length === 0) return;

        event.preventDefault();
        setMobilePanelPath(path);
    }

    function scheduleTabDropdownClose() {
        clearTimeout(closeTimer.current);
        closeTimer.current = setTimeout(() => setHoveredTab(null), TAB_DROPDOWN_CLOSE_DELAY_MS);
    }

    // Itens curados substituem as categorias raiz nas abas.
    const hasHighlights = Boolean(highlightItems && highlightItems.length > 0);
    const tabs: CategoryNavItem[] = hasHighlights ? highlightItems! : categories;

    if (tabs.length === 0 && categories.length === 0) return null;

    return (
        <nav
            ref={navRef}
            className="w-full bg-secondary"
            aria-label="Categorias"
            data-variant="default"
            // A cor do fade das setas vem daqui — agnóstica, sem hardcode no ScrollNavFade.
            // Texto segue `text-secondary-content` do tema, como o header.
            style={{ ["--nav-fade-color" as string]: "var(--color-secondary)" }}
        >
            <div ref={barRef} className={cn("relative max-w-7xl px-0 md:px-4 md:mx-auto", headerWithExtraFooter ? "mx-2" : "")}>
                <ScrollableContainer showNav navVariant="edge-fade" navSize="sm" align="center" gap={0}>
                    {showOwnButton && categories.length > 0 && (
                        <button
                            type="button"
                            onClick={() => setOpen((v) => !v)}
                            aria-expanded={open}
                            aria-label={departmentsLabel}
                            className={`flex shrink-0 items-center gap-1 whitespace-nowrap px-4 py-3 text-sm font-semibold uppercase tracking-wide text-secondary-content outline-none transition-colors ${open ? "bg-secondary-content/15" : "hover:bg-secondary-content/10"}`}
                        >
                            {departmentsIcon ? (
                                <Icon name={departmentsIcon} size={16} className="mb-1 inline-block md:mr-4" />
                            ) : (
                                <IoMenuOutline size={16} className="mb-1 inline-block md:mr-4" />
                            )}
                            {/* Mobile: só o ícone — o label ocuparia o espaço das abas. */}
                            <span className="max-md:hidden">{departmentsLabel}</span>
                        </button>
                    )}

                    {tabs.map((cat) => (
                        <Link
                            key={cat.id}
                            href={cat.href ?? "#"}
                            // Hover só com mouse/caneta: o iOS emula `mouseenter`
                            // a cada toque e o dropdown roubava o 1º toque da aba.
                            onPointerEnter={(e) => {
                                if (e.pointerType !== "touch") openTabDropdown(cat, e.currentTarget);
                            }}
                            onPointerLeave={(e) => {
                                if (e.pointerType !== "touch") scheduleTabDropdownClose();
                            }}
                            onClick={(e) => handleTabClick(e, cat)}
                            className={cn(
                                "flex shrink-0 items-center gap-2 whitespace-nowrap px-6 text-sm font-semibold uppercase tracking-wide text-secondary-content transition-colors max-md:px-4 max-md:text-[0.7rem]",
                                cat.featured
                                    ? "mx-1 my-1 rounded-lg py-2 [--nav-featured-color:var(--color-accent)] animate-nav-featured-blink motion-reduce:animate-none motion-reduce:bg-accent/30"
                                    : "py-3 hover:bg-secondary-content/10"
                            )}
                        >
                            {hasHighlights && <Icon name={cat.icon} size={16} className="shrink-0" />}
                            {cat.label}
                        </Link>
                    ))}
                </ScrollableContainer>

                {open && (
                    <CategoryPanel layout={layout} categories={categories} onClose={() => setOpen(false)} title={departmentsLabel} />
                )}

                {hoveredTab && (
                    <div
                        ref={dropdownRef}
                        onMouseEnter={() => clearTimeout(closeTimer.current)}
                        onMouseLeave={scheduleTabDropdownClose}
                        style={{ left: dropdownLeft, maxHeight: dropdownMaxHeight }}
                        // `w-max`: largura do conteúdo (subcategorias + arte/vitrine), sem
                        // cortar nada; `max-w-full` segura na largura da barra.
                        className="absolute top-full z-50 flex w-max max-w-full animate-category-dropdown-in overflow-hidden rounded-b-box bg-base-100 shadow-xl motion-reduce:animate-none"
                    >
                        {/* `key`: trocar de aba reseta o drill-down interno do card. */}
                        <CategoryDetailCard
                            key={hoveredTab.category.id}
                            category={hoveredTab.category}
                            onClose={() => setHoveredTab(null)}
                        />
                    </div>
                )}

                {mobilePanelPath && (
                    <CategoryPanelMobile
                        categories={categories}
                        initialPath={mobilePanelPath}
                        onClose={() => setMobilePanelPath(null)}
                    />
                )}
            </div>
        </nav>
    );
}
